import { isNumber } from 'lodash-es';

import { generateColumns, getNameCount } from './generate-columns.js';
import { escapeForCSV } from '../../../../core/utils/download.js';
import { TimeseriesDatapoint } from '../../../core/types/timeseries.js';
import { getUnitAsString } from '../../../core/utils/get-unit-as-string.js';
import { singleLineName } from '../../../core/utils/single-line-name.js';
import { TimeseriesRawDataPerVariant } from '../../context/TimeseriesRawData.context.js';
import {
  TimeseriesBandDataPoint,
  TimeseriesBandSlotProps,
} from '../../types/timeseries-band.js';
import { ChartVariant } from '../../types/timeseries-props.js';
import { TimeseriesVariantSlotProps } from '../../types/timeseries.js';

type DataPointValues = keyof TimeseriesDatapoint;

/**
 * Populates array of names for each name of the series
 *
 * @param maxNameCount -
 * @param series -
 *
 * @returns array of values
 */
export function fillRowNames(
  maxNameCount: number,
  series: TimeseriesVariantSlotProps | TimeseriesBandSlotProps,
): string[] {
  const names = new Array(maxNameCount).fill('');

  if (Array.isArray(series.name)) {
    series.name.forEach((name, index) => {
      names[index] = escapeForCSV(name);
    });
  } else {
    names[0] = escapeForCSV(series.name);
  }

  return names;
}

const parseValueForCsv = (value: Date | undefined | number) => {
  if (value instanceof Date) {
    return value.toISOString();
  } else if (isNumber(value)) {
    return value.toString();
  }

  return '';
};

export function formatCSV(rawData: TimeseriesRawDataPerVariant) {
  const maxNameCount = getNameCount(rawData);
  const columns = generateColumns(rawData, maxNameCount);

  const dataPointColumns = columns.slice(columns.indexOf('start'));

  let csv = `${columns.join(',')}\n`;

  for (const variant of Object.keys(rawData) as ChartVariant[]) {
    for (const series of rawData[variant as ChartVariant | 'band']) {
      const names = fillRowNames(maxNameCount, series);
      const joinedNames = singleLineName(names);

      const seriesRow = names.concat([
        joinedNames,
        getUnitAsString(series.unit),
      ]);

      for (const dataPoint of series.datapoints) {
        const dataPointRow: string[] = [];

        for (let i = 0; i < dataPointColumns.length; i++) {
          const column: DataPointValues = dataPointColumns[
            i
          ] as DataPointValues;
          const value =
            dataPoint[
              column as keyof (TimeseriesDatapoint | TimeseriesBandDataPoint)
            ];

          const parsedValue = parseValueForCsv(value);
          dataPointRow.push(parsedValue);
        }

        csv += `${seriesRow.concat(dataPointRow).join(',')}\n`;
      }
    }
  }

  return csv;
}
